/**
 * CryoVault Detail Modal
 *
 * Full-screen detail view for a frozen storage group.
 * Shows structured metadata parsed from specimen notes and
 * provides an "Edit" action to open the AddFrozenStorageModal.
 */

import { useMemo, useState, useCallback } from "react";
import {
  Modal,
  Pressable,
  View,
  ScrollView,
  Platform,
  ActivityIndicator,
  Linking,
} from "react-native";
import { Text } from "@louise/ui/Text/Text";
import { LinearGradient } from "expo-linear-gradient";
import {
  X,
  Thermometer,
  MapPin,
  Calendar,
  Pencil,
  Snowflake,
  FlaskConical,
  Hash,
  FileText,
  Download,
  type LucideIcon,
} from "lucide-react-native";
import { useDocuments, useGetDownloadUrl } from "@src/packages/documents";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { StorageItem } from "./CryoVaultSection";
import {
  parseNotesString,
  parseStorageLocationMeta,
  type ParsedNotesData,
  FREEZING_METHODS,
  EMBRYO_DAY_OPTIONS,
  FERTILISATION_METHODS,
  PGT_OPTIONS,
  GENETIC_STATUSES,
  EMBRYO_GRADES,
  SEX_OPTIONS,
  EMBRYO_STATUSES,
  OOCYTE_STATUSES,
  COUNTRIES,
  STORAGE_LOCATION_TYPES,
  type DropdownOption,
} from "@src/packages/care/components/frozen-storage-data";
import { formatDate as formatLocalizedDate } from "@src/packages/i18n/formatters";
import { useLocale } from "@src/packages/i18n/useLocale";
import type { Locale } from "@src/packages/i18n/locale";

export type CryoVaultDetailModalProps = {
  open: boolean;
  onClose: () => void;
  item: StorageItem | null;
  onEdit?: (item: StorageItem) => void;
};

function labelFor(options: DropdownOption[], id: string): string {
  return options.find((o) => o.id === id)?.label ?? id;
}

function formatDateDisplay(dateString: string, locale: Locale): string {
  try {
    const [y, m, d] = dateString.split("-").map(Number);
    const date = new Date(y!, m! - 1, d!);
    return formatLocalizedDate(date, locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
}

/** A single detail row */
function DetailRow({
  label,
  value,
  color,
}: {
  label: string;
  value: string | undefined | null;
  color?: string;
}) {
  if (!value) return null;
  return (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: "rgba(255, 255, 255, 0.04)",
      }}
    >
      <Text style={{ fontSize: 12, color: "rgba(255, 255, 255, 0.5)" }}>
        {label}
      </Text>
      <Text
        style={{
          fontSize: 12,
          fontWeight: "500",
          color: color || "rgba(255, 255, 255, 0.85)",
        }}
      >
        {value}
      </Text>
    </View>
  );
}

/** Section header */
function SectionHeader({
  title,
  icon: Icon,
}: {
  title: string;
  icon?: LucideIcon;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginBottom: 8,
        marginTop: 16,
      }}
    >
      {Icon && <Icon size={12} color="#64748B" />}
      <Text
        style={{
          fontSize: 10,
          fontWeight: "700",
          color: "#64748B",
          textTransform: "uppercase",
          letterSpacing: 1,
        }}
      >
        {title}
      </Text>
    </View>
  );
}

/** Individual embryo mini card */
function EmbryoMiniCard({
  embryo,
  index,
  typeColor,
}: {
  embryo: NonNullable<ParsedNotesData["individualEmbryos"]>[number];
  index: number;
  typeColor: string;
}) {
  return (
    <View
      style={{
        borderRadius: 10,
        borderWidth: 1,
        borderColor: `${typeColor}20`,
        backgroundColor: `${typeColor}08`,
        padding: 10,
      }}
    >
      <View
        style={{ flexDirection: "row", alignItems: "center", marginBottom: 6 }}
      >
        <Text style={{ fontSize: 11, fontWeight: "600", color: typeColor }}>
          Embryo #{index + 1}
        </Text>
        {embryo.embryoId && (
          <Text
            style={{
              fontSize: 10,
              color: "rgba(255,255,255,0.5)",
              marginLeft: 6,
            }}
          >
            ID: {embryo.embryoId}
          </Text>
        )}
      </View>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 4 }}>
        {embryo.grade && (
          <View
            style={{
              paddingHorizontal: 6,
              paddingVertical: 2,
              borderRadius: 4,
              backgroundColor: `${typeColor}15`,
            }}
          >
            <Text style={{ fontSize: 9, fontWeight: "600", color: typeColor }}>
              {labelFor(EMBRYO_GRADES, embryo.grade)}
            </Text>
          </View>
        )}
        {embryo.geneticStatus && embryo.geneticStatus !== "not_tested" && (
          <View
            style={{
              paddingHorizontal: 6,
              paddingVertical: 2,
              borderRadius: 4,
              backgroundColor: `${typeColor}15`,
            }}
          >
            <Text style={{ fontSize: 9, fontWeight: "600", color: typeColor }}>
              {labelFor(GENETIC_STATUSES, embryo.geneticStatus)}
            </Text>
          </View>
        )}
        {embryo.sex && embryo.sex !== "unknown" && (
          <View
            style={{
              paddingHorizontal: 6,
              paddingVertical: 2,
              borderRadius: 4,
              backgroundColor: `${typeColor}15`,
            }}
          >
            <Text style={{ fontSize: 9, fontWeight: "600", color: typeColor }}>
              {labelFor(SEX_OPTIONS, embryo.sex)}
            </Text>
          </View>
        )}
        {embryo.status && (
          <View
            style={{
              paddingHorizontal: 6,
              paddingVertical: 2,
              borderRadius: 4,
              backgroundColor: "rgba(255,255,255,0.06)",
            }}
          >
            <Text
              style={{
                fontSize: 9,
                fontWeight: "500",
                color: "rgba(255,255,255,0.6)",
              }}
            >
              {labelFor(EMBRYO_STATUSES, embryo.status)}
            </Text>
          </View>
        )}
      </View>
      {embryo.notes && (
        <Text
          style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 4 }}
        >
          {embryo.notes}
        </Text>
      )}
    </View>
  );
}

export function CryoVaultDetailModal({
  open,
  onClose,
  item,
  onEdit,
}: CryoVaultDetailModalProps) {
  const { locale } = useLocale();
  const capturePress = usePressCapture("CryoVaultDetailModal");
  const parsed = useMemo(() => {
    if (!item || item.notes.length === 0) return {} as ParsedNotesData;
    return parseNotesString(item.notes[0]!);
  }, [item]);

  const storageMeta = useMemo(
    () => parseStorageLocationMeta(item?.storageLocationNotes),
    [item?.storageLocationNotes],
  );

  // Document download support
  const { data: allDocuments = [] } = useDocuments();
  const getDownloadUrl = useGetDownloadUrl();
  const [downloadingUploadId, setDownloadingUploadId] = useState<string | null>(
    null,
  );

  // Match parsed file names to actual Document records tagged "cryo-storage"
  const matchedDocuments = useMemo(() => {
    if (!parsed.fileNames || parsed.fileNames.length === 0) return [];
    const cryoDocs = allDocuments.filter((doc) =>
      doc.tags?.includes("cryo-storage"),
    );
    return parsed.fileNames.map((fileName) => {
      const doc = cryoDocs.find((d) => d.name?.includes(fileName));
      const upload = doc?.uploads?.[0];
      return { fileName, uploadId: upload?.id ?? null };
    });
  }, [parsed.fileNames, allDocuments]);

  const handleDownload = useCallback(
    async (uploadId: string) => {
      setDownloadingUploadId(uploadId);
      try {
        const url = await getDownloadUrl.mutateAsync({
          uploadId,
          disposition: "attachment",
        });
        if (url) {
          await Linking.openURL(url);
        }
      } catch (err) {
        console.warn("CryoVault: failed to download file", err);
      } finally {
        setDownloadingUploadId(null);
      }
    },
    [getDownloadUrl],
  );

  if (!item) return null;

  const typeColor = item.type === "eggs" ? "#38BDF8" : "#F472B6";
  const clinicName = parsed.clinicName || item.facility;
  const isEmbryos = item.type === "embryos";
  const displayCountry = storageMeta?.country || parsed.country;
  const displayLocation = storageMeta?.location || parsed.storageCity;
  const displayCountryName = storageMeta?.countryName;
  const displayFacilityType =
    storageMeta?.facilityType || parsed.storageLocationType;
  const displayStorageId = storageMeta?.storageId || parsed.storageId;

  return (
    <Modal
      visible={open}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        onPress={() => {
          capturePress("modal_backdrop_dismiss");
          onClose();
        }}
        style={{ flex: 1, backgroundColor: "rgba(2, 6, 23, 0.85)" }}
      >
        {Platform.OS === "web" && (
          <View
            style={{
              position: "absolute",
              inset: 0,
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          />
        )}

        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              maxHeight: "90%",
              borderRadius: 24,
              overflow: "hidden",
              borderWidth: 1,
              borderColor: `${typeColor}25`,
              backgroundColor: "rgba(11, 17, 33, 0.98)",
            }}
          >
            {/* Frost header */}
            <LinearGradient
              colors={[`${typeColor}18`, "rgba(15, 23, 42, 0.95)"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              style={{
                paddingTop: 20,
                paddingBottom: 16,
                paddingHorizontal: 20,
              }}
            >
              {/* Close + Edit buttons */}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 16,
                }}
              >
                <Pressable
                  onPress={() => {
                    capturePress("close_cryo_vault_detail");
                    onClose();
                  }}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <X size={16} color="#94A3B8" />
                </Pressable>
                {onEdit && (
                  <Pressable
                    onPress={() => {
                      capturePress("edit_frozen_storage");
                      onEdit(item);
                    }}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 5,
                      paddingHorizontal: 12,
                      paddingVertical: 6,
                      borderRadius: 10,
                      backgroundColor: `${typeColor}15`,
                      borderWidth: 1,
                      borderColor: `${typeColor}30`,
                    }}
                  >
                    <Pencil size={12} color={typeColor} />
                    <Text
                      style={{
                        fontSize: 12,
                        fontWeight: "600",
                        color: typeColor,
                      }}
                    >
                      Edit
                    </Text>
                  </Pressable>
                )}
              </View>

              {/* Orb + count + clinic */}
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <View
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 26,
                    backgroundColor: `${typeColor}15`,
                    borderWidth: 1,
                    borderColor: `${typeColor}30`,
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 14,
                  }}
                >
                  <View
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      backgroundColor: typeColor,
                      shadowColor: typeColor,
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: 0.6,
                      shadowRadius: 10,
                    }}
                  />
                  {/* Count badge */}
                  <View
                    style={{
                      position: "absolute",
                      top: -4,
                      right: -6,
                      backgroundColor: typeColor,
                      borderRadius: 10,
                      minWidth: 22,
                      height: 22,
                      alignItems: "center",
                      justifyContent: "center",
                      paddingHorizontal: 5,
                      shadowColor: typeColor,
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: 0.5,
                      shadowRadius: 8,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 11,
                        fontWeight: "800",
                        color: "#020617",
                      }}
                    >
                      {item.count}
                    </Text>
                  </View>
                </View>

                <View style={{ flex: 1 }}>
                  <Text
                    numberOfLines={2}
                    style={{
                      fontSize: 18,
                      fontWeight: "600",
                      color: "#ffffff",
                      letterSpacing: -0.3,
                    }}
                  >
                    {clinicName}
                  </Text>
                  <Text
                    style={{
                      fontSize: 12,
                      color: typeColor,
                      fontWeight: "500",
                      marginTop: 2,
                    }}
                  >
                    {item.count} {item.type} preserved
                  </Text>
                </View>

                {/* Temperature badge */}
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 3,
                    paddingHorizontal: 7,
                    paddingVertical: 4,
                    borderRadius: 8,
                    backgroundColor: "rgba(56, 189, 248, 0.1)",
                  }}
                >
                  <Thermometer size={10} color="#38BDF8" />
                  <Text
                    style={{ fontSize: 9, color: "#38BDF8", fontWeight: "600" }}
                  >
                    -196°C
                  </Text>
                </View>
              </View>
            </LinearGradient>

            {/* Scrollable detail content */}
            <ScrollView
              style={{ flex: 1 }}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{
                paddingHorizontal: 20,
                paddingBottom: 24,
              }}
            >
              {/* Location & Storage */}
              <SectionHeader title="Location & Storage" icon={MapPin} />
              <View
                style={{
                  borderRadius: 12,
                  backgroundColor: "rgba(255,255,255,0.025)",
                  padding: 12,
                }}
              >
                <DetailRow label="Clinic" value={clinicName} />
                <DetailRow label="Location" value={displayLocation} />
                <DetailRow
                  label="Country"
                  value={
                    displayCountryName ||
                    (displayCountry
                      ? labelFor(COUNTRIES, displayCountry)
                      : undefined)
                  }
                />
                <DetailRow
                  label="Storage Type"
                  value={
                    displayFacilityType
                      ? labelFor(STORAGE_LOCATION_TYPES, displayFacilityType)
                      : undefined
                  }
                />
                <DetailRow label="Storage ID" value={displayStorageId} />
                <DetailRow
                  label="Facility"
                  value={
                    item.facility !== clinicName ? item.facility : undefined
                  }
                />
              </View>

              {/* Dates */}
              <SectionHeader title="Dates" icon={Calendar} />
              <View
                style={{
                  borderRadius: 12,
                  backgroundColor: "rgba(255,255,255,0.025)",
                  padding: 12,
                }}
              >
                {isEmbryos ? (
                  <>
                    <DetailRow
                      label="Fertilisation Date"
                      value={
                        parsed.fertilisationDate
                          ? formatDateDisplay(parsed.fertilisationDate, locale)
                          : undefined
                      }
                    />
                    <DetailRow
                      label="Freeze Date"
                      value={
                        parsed.freezeDate
                          ? formatDateDisplay(parsed.freezeDate, locale)
                          : undefined
                      }
                    />
                  </>
                ) : (
                  <>
                    <DetailRow
                      label="Retrieval Date"
                      value={
                        parsed.retrievalDate
                          ? formatDateDisplay(parsed.retrievalDate, locale)
                          : undefined
                      }
                    />
                    <DetailRow
                      label="Freeze Date"
                      value={
                        parsed.freezeDate
                          ? formatDateDisplay(parsed.freezeDate, locale)
                          : undefined
                      }
                    />
                  </>
                )}
                <DetailRow label="Stored Since" value={item.storedDate} />
              </View>

              {/* Method & Details */}
              <SectionHeader
                title={isEmbryos ? "Embryo Details" : "Freezing Details"}
                icon={Snowflake}
              />
              <View
                style={{
                  borderRadius: 12,
                  backgroundColor: "rgba(255,255,255,0.025)",
                  padding: 12,
                }}
              >
                {isEmbryos ? (
                  <>
                    <DetailRow
                      label="Day of Development"
                      value={
                        parsed.dayOfDevelopment
                          ? labelFor(
                              EMBRYO_DAY_OPTIONS,
                              parsed.dayOfDevelopment,
                            )
                          : undefined
                      }
                      color={typeColor}
                    />
                    <DetailRow
                      label="Fertilisation Method"
                      value={
                        parsed.fertilisationMethod
                          ? labelFor(
                              FERTILISATION_METHODS,
                              parsed.fertilisationMethod,
                            )
                          : undefined
                      }
                    />
                    <DetailRow
                      label="Genetic Testing (PGT)"
                      value={
                        parsed.pgtPerformed
                          ? labelFor(PGT_OPTIONS, parsed.pgtPerformed)
                          : undefined
                      }
                      color={
                        parsed.pgtPerformed &&
                        parsed.pgtPerformed !== "not_performed"
                          ? typeColor
                          : undefined
                      }
                    />
                    <DetailRow
                      label="Freezing Method"
                      value={
                        parsed.freezingMethod
                          ? labelFor(FREEZING_METHODS, parsed.freezingMethod)
                          : undefined
                      }
                    />
                  </>
                ) : (
                  <>
                    <DetailRow
                      label="Freezing Method"
                      value={
                        parsed.freezingMethod
                          ? labelFor(FREEZING_METHODS, parsed.freezingMethod)
                          : undefined
                      }
                      color={typeColor}
                    />
                    <DetailRow
                      label="Current Status"
                      value={
                        parsed.currentStatus
                          ? labelFor(OOCYTE_STATUSES, parsed.currentStatus)
                          : undefined
                      }
                    />
                  </>
                )}
              </View>

              {/* Counts */}
              <SectionHeader title="Counts" icon={Hash} />
              <View
                style={{
                  borderRadius: 12,
                  backgroundColor: "rgba(255,255,255,0.025)",
                  padding: 12,
                }}
              >
                {isEmbryos ? (
                  <>
                    <DetailRow
                      label="Total Created"
                      value={parsed.totalEmbryosCreated?.toString()}
                    />
                    <DetailRow
                      label="Number Frozen"
                      value={parsed.numberFrozen?.toString()}
                      color={typeColor}
                    />
                    <DetailRow
                      label="Transferred"
                      value={parsed.numberTransferred?.toString()}
                    />
                    <DetailRow
                      label="Discarded"
                      value={parsed.numberDiscarded?.toString()}
                    />
                    <DetailRow
                      label="Currently in Storage"
                      value={
                        parsed.currentlyInStorage?.toString() ??
                        item.count.toString()
                      }
                      color={typeColor}
                    />
                  </>
                ) : (
                  <>
                    <DetailRow
                      label="Total Retrieved"
                      value={parsed.totalRetrieved?.toString()}
                    />
                    <DetailRow
                      label="Mature (MII)"
                      value={parsed.matureMII?.toString()}
                    />
                    <DetailRow
                      label="Immature (MI)"
                      value={parsed.immatureMI?.toString()}
                    />
                    <DetailRow
                      label="Degenerated"
                      value={parsed.degenerated?.toString()}
                    />
                    <DetailRow
                      label="Number Frozen"
                      value={parsed.numberFrozen?.toString()}
                      color={typeColor}
                    />
                    <DetailRow
                      label="Number Thawed"
                      value={parsed.numberThawed?.toString()}
                    />
                    <DetailRow
                      label="Post-Thaw Survival"
                      value={
                        parsed.postThawSurvivalRate != null
                          ? `${parsed.postThawSurvivalRate}%`
                          : undefined
                      }
                    />
                  </>
                )}
              </View>

              {/* Individual Embryos */}
              {isEmbryos &&
                parsed.individualEmbryos &&
                parsed.individualEmbryos.length > 0 && (
                  <>
                    <SectionHeader
                      title="Individual Embryos"
                      icon={FlaskConical}
                    />
                    <View style={{ gap: 8 }}>
                      {parsed.individualEmbryos.map((embryo, idx) => (
                        <EmbryoMiniCard
                          key={`embryo-${idx}`}
                          embryo={embryo}
                          index={idx}
                          typeColor={typeColor}
                        />
                      ))}
                    </View>
                  </>
                )}

              {/* Notes */}
              {parsed.freeTextNotes && (
                <>
                  <SectionHeader title="Notes" icon={FileText} />
                  <View
                    style={{
                      borderRadius: 12,
                      backgroundColor: "rgba(255,255,255,0.025)",
                      padding: 12,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 12,
                        color: "rgba(255,255,255,0.7)",
                        lineHeight: 18,
                      }}
                    >
                      {parsed.freeTextNotes}
                    </Text>
                  </View>
                </>
              )}

              {/* Documents */}
              {matchedDocuments.length > 0 && (
                <>
                  <SectionHeader title="Documents" icon={FileText} />
                  <View
                    style={{
                      borderRadius: 12,
                      backgroundColor: "rgba(255,255,255,0.025)",
                      padding: 12,
                      gap: 6,
                    }}
                  >
                    {matchedDocuments.map((doc, idx) => (
                      <Pressable
                        key={`file-${idx}`}
                        onPress={
                          doc.uploadId
                            ? () => {
                                capturePress(
                                  pressLabel(doc.fileName),
                                  "download_specimen_document",
                                );
                                void handleDownload(doc.uploadId!);
                              }
                            : undefined
                        }
                        disabled={
                          !doc.uploadId || downloadingUploadId === doc.uploadId
                        }
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                          paddingVertical: 6,
                          paddingHorizontal: 4,
                          borderRadius: 8,
                          opacity: doc.uploadId ? 1 : 0.6,
                        }}
                      >
                        <FileText size={12} color={typeColor} />
                        <Text
                          numberOfLines={1}
                          style={{
                            fontSize: 12,
                            color: "rgba(255,255,255,0.7)",
                            flex: 1,
                          }}
                        >
                          {doc.fileName}
                        </Text>
                        {doc.uploadId &&
                          (downloadingUploadId === doc.uploadId ? (
                            <ActivityIndicator size="small" color={typeColor} />
                          ) : (
                            <Download size={14} color={typeColor} />
                          ))}
                      </Pressable>
                    ))}
                  </View>
                </>
              )}

              {/* Specimen count footer */}
              <View style={{ marginTop: 16, alignItems: "center" }}>
                <Text style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
                  {item.specimenIds.length} specimen record
                  {item.specimenIds.length !== 1 ? "s" : ""}
                </Text>
              </View>
            </ScrollView>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  );
}
